import React from "react";
import { LIFE_REBUILD_REPORT_PRICE_USD } from "./report-types";

import type { ReportCheckoutButtonProps } from "../../paid-reports";

export type { ReportCheckoutButtonProps };

// The rebuild order is decided when the report is written (report-prompt.ts, pass 1),
// so the result page can only show its shape: position 1 is the 14-day plan,
// positions 2 and 3 fill the rest of the first 90 days, 4 to 6 come after.
const SEQUENCE_ROWS = [
  { position: 1, when: "Weeks 1 to 2, as a 14-day plan" },
  { position: 2, when: "Next, inside your first 90 days" },
  { position: 3, when: "Then, to finish your first 90 days" },
];

const PLAN_DAYS = Array.from({ length: 14 }, (_, index) => index + 1);

const ALSO_INSIDE = [
  "A deep dive on each of your six domains, with the first move for each",
  "Your 30, 60 and 90 day markers, so you can tell the rebuild is holding",
  "Your watch-outs: how your pattern tends to derail, and what to do instead",
];

const FAQ = [
  { question: "Is this a subscription?", answer: `No. One payment of $${LIFE_REBUILD_REPORT_PRICE_USD}. Nothing renews.` },
  {
    question: "How is it different from my free score?",
    answer: "Your score shows where you stand. The report gives you the order, the first 14 days and the markers.",
  },
  { question: "What if it doesn't help?", answer: "Email support@lifehack.org within 30 days for a full refund." },
];

const BUY_BUTTON_CLASS =
  "w-full min-h-[52px] rounded-lg px-6 text-lg font-bold text-white bg-[#2A6354] hover:bg-[#1f4a3f] transition-colors shadow-lg disabled:opacity-60";

function LockIcon() {
  return (
    <svg className="w-4 h-4 text-gray-500 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-5 h-5 text-[#2A6354] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-4 h-4 text-[#2A6354]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

interface InlineReportOfferProps {
  runId: string;
  email?: string;
  ReportCheckoutButton: React.ComponentType<ReportCheckoutButtonProps>;
}

/** The $19 report, sold on the result page itself with checkout opening in place. */
export function InlineReportOffer({ runId, email, ReportCheckoutButton }: InlineReportOfferProps) {
  return (
    <>
      <section
        id="life-rebuild-report"
        aria-labelledby="life-rebuild-report-heading"
        className="bg-white rounded-2xl border-2 border-[#2A6354] shadow-lg p-6 md:p-8 space-y-7"
      >
        <div className="space-y-2">
          <p className="text-sm uppercase tracking-wide text-[#2A6354] font-semibold">Your Life Rebuild Report</p>
          <h2 id="life-rebuild-report-heading" className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
            The order to rebuild, written for your answers
          </h2>
          <p className="text-base text-gray-600">
            Your score shows where you stand. The report shows what to do first, second and third, and what to leave
            for later.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">Your rebuild sequence</h3>
          <ol className="space-y-2">
            {SEQUENCE_ROWS.map((row) => (
              <li key={row.position} className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2.5">
                <span className="w-6 h-6 rounded-full bg-[#2A6354] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {row.position}
                </span>
                <span className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-400 select-none blur-[4px]" aria-hidden="true">
                    Domain name
                  </span>
                  <span className="text-xs text-gray-600">{row.when}</span>
                </span>
                <LockIcon />
              </li>
            ))}
          </ol>
          <p className="text-sm text-gray-600">Plus your other three domains, in the order that holds for you after day 90.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">Your first 14 days, day by day</h3>
          <div className="grid grid-cols-7 gap-1.5" aria-hidden="true">
            {PLAN_DAYS.map((day) => (
              <div
                key={day}
                className={
                  day === 1
                    ? "h-11 rounded-md bg-[#2A6354] text-white flex items-center justify-center text-sm font-bold"
                    : "h-11 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center text-sm font-semibold"
                }
              >
                {day}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-700">One small action a day, built around the anchor you defend first.</p>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Also inside</h3>
          <ul className="space-y-2.5">
            {ALSO_INSIDE.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-gray-800">
                <CheckIcon />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 pt-6 flex flex-col items-center gap-3 text-center">
          <p className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-gray-900">${LIFE_REBUILD_REPORT_PRICE_USD}</span>
            <span className="text-base text-gray-600">one time, yours to keep</span>
          </p>
          <div className="w-full sm:max-w-sm">
            <ReportCheckoutButton runId={runId} email={email} assessmentType="life-rebuild-score" ctaLocation="inline_offer" className={BUY_BUTTON_CLASS}>
              Get My Rebuild Plan
            </ReportCheckoutButton>
          </div>
          <p className="flex items-center gap-1.5 text-sm text-gray-700">
            <ShieldIcon />
            30-day money-back guarantee
          </p>
          <p className="text-xs text-gray-600">
            No subscription. Secure checkout opens right here, and your report arrives on screen and by email.
          </p>
        </div>
      </section>

      <section aria-label="Questions about the report" className="bg-white rounded-2xl border border-gray-200 px-6 py-2">
        {FAQ.map((item, index) => (
          <div key={item.question} className={index < FAQ.length - 1 ? "py-4 border-b border-gray-200" : "py-4"}>
            <h3 className="text-base font-semibold text-gray-900">{item.question}</h3>
            <p className="text-sm text-gray-600 mt-1">{item.answer}</p>
          </div>
        ))}
      </section>
    </>
  );
}
